import AcademicLevelSelect from "./AcademicLevelSelect";
import { buildUserUpdatePayload } from "../utils/userPayload";
import { useContext, useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import { IoClose } from "react-icons/io5";
import { FaLock, FaRegUser, FaSave, FaUserCircle } from "react-icons/fa";
import { DarkModeContext } from "../Context/DarkModeContext.jsx";
import axiosInstance from "../Context/axiosInstance.js";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { ClipLoader } from "react-spinners";
import { useAuth } from "../Context/AuthContext";

export default function EditProfileModal({
  showModal,
  setShowModal,
  onUpdateUsername,
}) {
  const { isDarkMode } = useContext(DarkModeContext);
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [userInfo, setUserInfo] = useState(null);
  const [originalUserInfo, setOriginalUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const userId = localStorage.getItem("userId");

  const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

  const encryptData = (data) => {
    return CryptoJS.AES.encrypt(data, SECRET_KEY).toString();
  };

  const decryptData = (encryptedData) => {
    const bytes = CryptoJS.AES.decrypt(
      encryptedData,
      process.env.REACT_APP_SECRET_KEY,
    );
    return bytes.toString(CryptoJS.enc.Utf8);
  };

  // Fetch user info when modal opens
  useEffect(() => {
    if (!showModal) return;
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `/users/${decryptData(userId)}`,
        );
        setUserInfo(response.data);
        setOriginalUserInfo(response.data);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [showModal, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prevInfo) => ({ ...prevInfo, [name]: name === "academicLevelId" ? Number(value) : value }));
  };

  const hasChanges = () =>
    JSON.stringify(userInfo) !== JSON.stringify(originalUserInfo);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    const academicLevelChanged =
      userInfo?.academicLevelId !== originalUserInfo?.academicLevelId;

    try {
      await axiosInstance.put(`/users/${decryptData(userId)}`, buildUserUpdatePayload(userInfo));
      const response = await axiosInstance.get(`/users/${decryptData(userId)}`);
      const latestUser = response.data;

      localStorage.setItem("userName", latestUser.name);

      // Call callback to update Layout state
      if (onUpdateUsername) onUpdateUsername(latestUser.name);
      localStorage.setItem(
        "academiclvl",
        encryptData(latestUser.academicLevel),
      );

      toast.success("Profile successfully updated!");
      setShowModal(false);

      if (academicLevelChanged) {
        toast.info("Academic level changed. Please sign in again.");
        logout();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    } finally {
      setUpdating(false);
    }
  };

  const getInputType = (key) => {
    switch (key) {
      case "email":
        return "email";
      case "password":
        return "password";
      case "guardianPhoneNumber":
      case "phoneNumber":
        return "tel";
      default:
        return "text";
    }
  };

  return (
    <AnimatePresence>
      {showModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[999] bg-slate-950/65 p-4 backdrop-blur-sm overflow-y-auto"
          onClick={() => setShowModal(false)}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            layout
            className={`relative w-full max-w-[720px] max-h-[92vh] overflow-hidden rounded-3xl border shadow-2xl ${
              isDarkMode
                ? "border-gray-700 bg-[#191919] text-gray-100"
                : "border-white/70 bg-white text-gray-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button inside modal */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/15 text-white transition hover:rotate-90 hover:bg-black/30"
              aria-label="Close profile settings"
            >
              <IoClose size={23} />
            </button>

            <div
              className={`px-6 pb-14 pt-7 text-white ${
                isDarkMode
                  ? "bg-gradient-to-r from-red-700 to-customRed"
                  : "bg-gradient-to-r from-[#06315B] to-blue-500"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 shadow-inner backdrop-blur-sm">
                  <FaUserCircle size={35} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Account settings</h2>
                  <p className="mt-1 text-sm text-white/75">
                    Keep your personal and learning details up to date.
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div
              className={`relative mx-6 -mt-6 flex rounded-2xl border p-1.5 shadow-lg ${
                isDarkMode
                  ? "border-gray-700 bg-[#242424]"
                  : "border-gray-100 bg-white"
              }`}
            >
              {["profile", "password"].map((tab) => (
                <button
                  type="button"
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    activeTab === tab
                      ? isDarkMode
                        ? "bg-customRed text-white shadow-md"
                        : "bg-[#06315B] text-white shadow-md"
                      : isDarkMode
                        ? "text-gray-400 hover:bg-white/5 hover:text-white"
                        : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                  }`}
                >
                  {tab === "profile" ? <FaRegUser /> : <FaLock />}
                  {tab === "profile" ? "Profile details" : "Password"}
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div className="max-h-[calc(92vh-190px)] overflow-y-auto px-6 pb-6 pt-5 sm:px-8">
              {loading ? (
                <div className="flex flex-col items-center justify-center min-h-[300px]">
                  <ClipLoader size={60} color="#3b82f6" />
                  <p className="mt-4 text-gray-400">Loading user data...</p>
                </div>
              ) : (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  layout
                >
                  {activeTab === "profile" && userInfo && (
                    <form onSubmit={handleSubmit}>
                      <div className="mb-4">
                        <h3 className="font-bold">Personal information</h3>
                        <p className="mt-1 text-xs text-gray-500">
                          Fields shown here come from your account profile.
                        </p>
                      </div>
                      <div className="grid gap-x-4 sm:grid-cols-2">
                        {Object.keys(userInfo)
                          .filter(
                            (key) =>
                              ![
                                "id",
                                "academicLevel",
                                "password",
                                "storeId",
                                "roles",
                                "createdAt",
                                "updatedAt",
                              ].includes(key),
                          )
                          .map((key) => (
                            <div
                              key={key}
                              className={key === "email" ? "sm:col-span-2" : ""}
                            >
                              <label
                                htmlFor={key}
                                className="mb-1.5 mt-2 block text-xs font-bold uppercase tracking-wide text-gray-500"
                              >
                                {key === "academicLevelId" ? "Academic Level" : key.charAt(0).toUpperCase() +
                                  key.slice(1).replace(/([A-Z])/g, " $1")}
                              </label>
                              {key === "academicLevelId" ? (
                        <AcademicLevelSelect id={key} name={key} value={userInfo[key]} onChange={handleChange} className="mt-1 p-2 w-full border rounded bg-white text-gray-900" />
                      ) : key === "gender" ? (
                        <select id={key} name={key} value={userInfo[key]} onChange={handleChange} className="mt-1 p-2 w-full border rounded bg-white text-gray-900">
                          <option value="Male">Male</option><option value="Female">Female</option>
                        </select>
                              ) : (
                                <input
                                  type={getInputType(key)}
                                  id={key}
                                  name={key}
                                  value={userInfo[key]}
                                  onChange={handleChange}
                                  className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
                                    isDarkMode
                                      ? "bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-400"
                                      : "bg-white border-gray-300 text-gray-700 placeholder-gray-500"
                                  }`}
                                />
                              )}
                            </div>
                          ))}
                      </div>

                      <div
                        className={`mt-6 mb-4 md:mb-0 flex items-center justify-center md:justify-between border-t pt-4 ${isDarkMode ? "border-gray-700 bg-[#191919]" : "border-gray-100 bg-white"}`}
                      >
                        <p className="hidden text-xs text-gray-400 sm:block">
                          Academic-level changes require signing in again.
                        </p>
                        <button
                          type="submit"
                          className={`flex justify-center items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition ${
                            updating || !hasChanges()
                              ? "bg-blue-500 text-white opacity-50 cursor-not-allowed"
                              : "bg-blue-500 text-white hover:bg-blue-600"
                          }`}
                          disabled={updating || !hasChanges()}
                        >
                          {!updating && <FaSave />}
                          {updating ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  )}

                  {activeTab === "password" && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        toast.info("Password change coming soon.");
                      }}
                      className="flex flex-col gap-4"
                    >
                      <div className="mb-1">
                        <h3 className="font-bold">Change password</h3>
                        <p className="mt-1 text-xs text-gray-500">
                          Use a strong password you do not use elsewhere.
                        </p>
                      </div>
                      {[
                        "Current Password",
                        "New Password",
                        "Confirm Password",
                      ].map((label, index) => (
                        <div key={index}>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}
                          >
                            {label}
                          </label>
                          <input
                            type="password"
                            className={`w-full rounded-xl border px-3 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
                              isDarkMode
                                ? "bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-400"
                                : "bg-white border-gray-300 text-gray-700 placeholder-gray-500"
                            }`}
                          />
                        </div>
                      ))}

                      <div className="flex justify-center mt-5 mb-5 md:mb-0">
                        <button
                          type="submit"
                          className="flex items-center gap-2 rounded-xl bg-blue-500 px-6 py-2.5 font-semibold text-white transition hover:bg-blue-600"
                        >
                          Update Password
                        </button>
                      </div>
                    </form>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
